import type { ReactNode } from 'react'
import { AlertCircle, Copy, Inbox, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { errorDiagnostics, getErrorMessage, getErrorTitle, getRequestId } from '@/api/errors'

/**
 * The top of every page: kicker, title, one line of orientation, and the primary action.
 *
 * The rule the layout enforces is that there is exactly ONE obvious next thing to do per
 * screen. Actions sit hard right on the title's own baseline, so the primary button is the
 * heaviest thing in the top-right corner and reads before any of the filter chrome below.
 */
export function PageHeader({
  kicker,
  title,
  badge,
  description,
  actions,
  className,
}: {
  kicker?: string
  title: string
  /** Sits beside the title — a status or classification, not an action. */
  badge?: ReactNode
  description?: string
  actions?: ReactNode
  className?: string
}) {
  return (
    <header
      className={cn(
        'mb-5 flex flex-wrap items-end justify-between gap-x-6 gap-y-3 border-b border-border-soft pb-4 max-md:items-stretch',
        className,
      )}
    >
      <div className="min-w-0 max-md:w-full">
        {kicker ? <p className="kicker mb-1.5">{kicker}</p> : null}
        <div className="flex min-w-0 flex-wrap items-center gap-2.5">
          <h1 className="truncate text-xl font-semibold tracking-tight text-foreground max-md:overflow-visible max-md:whitespace-normal max-md:break-words">{title}</h1>
          {badge}
        </div>
        {description ? (
          <p className="mt-1.5 max-w-3xl text-sm text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2 max-md:w-full max-md:flex-wrap max-md:justify-start">{actions}</div> : null}
    </header>
  )
}

export function EmptyState({
  title,
  description,
  action,
  icon,
  className,
}: {
  title: string
  description?: string
  action?: ReactNode
  icon?: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-lg border border-dashed border-border-strong bg-surface-sunken px-6 py-14 text-center',
        className,
      )}
    >
      {/* The icon gets a brand-tinted plate rather than sitting loose in grey — an empty
          screen is often the first thing a new user sees, so it should still look like ours. */}
      <div className="mb-4 grid size-14 place-items-center rounded-full bg-brand-soft text-brand">
        {icon ?? <Inbox className="size-6" />}
      </div>
      <p className="text-lg font-semibold tracking-tight">{title}</p>
      {description ? (
        <p className="mt-1.5 max-w-sm text-sm text-muted-foreground">{description}</p>
      ) : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  )
}

export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown
  onRetry?: () => void
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center rounded-lg border border-danger/25 bg-danger-soft px-6 py-12 text-center',
        className,
      )}
      role="alert"
    >
      <div className="mb-4 grid size-14 place-items-center rounded-full bg-card text-danger shadow-e1">
        <AlertCircle className="size-6" />
      </div>
      <p className="text-lg font-semibold tracking-tight text-danger">{getErrorTitle(error)}</p>
      <p className="mt-1.5 max-w-md text-sm text-text-secondary">{getErrorMessage(error)}</p>
      {getRequestId(error) ? (
        <p className="mt-2 font-mono text-tiny text-text-muted">Request ID: {getRequestId(error)}</p>
      ) : null}
      <div className="mt-5 flex flex-wrap justify-center gap-2">
        {onRetry ? (
          <Button variant="outline" size="sm" className="bg-card" onClick={onRetry}>
            Try again
          </Button>
        ) : null}
        <Button
          variant="outline"
          size="sm"
          className="bg-card"
          onClick={() => void navigator.clipboard?.writeText(errorDiagnostics(error))}
        >
          <Copy className="size-3.5" />
          Copy diagnostics
        </Button>
      </div>
    </div>
  )
}

/**
 * The first thing anyone sees: signing in, finding the workspace, resolving access.
 *
 * One stable screen for all three, so the label changes in place instead of the whole page
 * flashing between three different spinners. Motion is switched off for people who ask for less.
 */
export function BrandLoader({ label = 'Loading…' }: { label?: string }) {
  return (
    <div role="status" aria-live="polite" className="flex min-h-dvh flex-col items-center justify-center gap-7 bg-background px-6">
      <div className="relative grid place-items-center">
        <span
          aria-hidden
          className="absolute size-32 rounded-full bg-gradient-to-br from-brand to-brand-accent opacity-[0.12] saturate-[0.35] blur-3xl motion-safe:animate-tt-breathe"
        />
        <img src="/brand/tapwork-mark.png" alt="" className="relative h-14 w-auto" />
      </div>
      <div aria-hidden className="relative h-1 w-44 overflow-hidden rounded-full bg-border">
        <span className="absolute inset-y-0 left-0 w-1/3 rounded-full bg-gradient-to-r from-brand via-brand to-brand-accent motion-safe:animate-tt-sweep" />
      </div>
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
    </div>
  )
}

/** What a page looks like before it arrives: a heading, a toolbar and a few cards, so nothing jumps when it lands. */
export function PageLoading() {
  return (
    <div role="status" aria-label="Loading" className="grid gap-6">
      <div className="grid gap-2">
        <Skeleton className="h-3 w-24 rounded-xs" />
        <Skeleton className="h-7 w-56 rounded-md" />
        <Skeleton className="h-4 w-80 max-w-full rounded-xs" />
      </div>
      <div className="flex gap-2">
        <Skeleton className="h-9 w-28 rounded-md" />
        <Skeleton className="h-9 w-40 rounded-md" />
        <Skeleton className="h-9 w-24 rounded-md" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-36 rounded-lg" />
        ))}
      </div>
    </div>
  )
}

export function LoadingState({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-base font-medium text-muted-foreground">
      <Loader2 className="size-7 animate-spin text-brand" />
      {label}
    </div>
  )
}

/**
 * A skeleton shaped like the table it replaces.
 *
 * A spinner tells you to wait; a skeleton tells you what is coming and stops the page
 * reflowing under the cursor when the rows land. Header first, then rows of the same height
 * the real ones will be.
 */
export function TableSkeleton({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card shadow-e1">
      <div className="flex items-center gap-4 border-b border-border bg-surface-sunken px-4 py-2.5">
        {Array.from({ length: columns }, (_, i) => (
          <Skeleton key={i} className="h-3 flex-1 rounded-xs" />
        ))}
      </div>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="flex items-center gap-4 border-b border-border-soft px-4 py-3 last:border-b-0">
          {Array.from({ length: columns }, (_, c) => (
            <Skeleton key={c} className={cn('h-3.5 flex-1 rounded-xs', c === 0 && 'flex-[1.6]')} />
          ))}
        </div>
      ))}
    </div>
  )
}

/** The same idea for a stack of rows that is not a table — the team tree, grouped lists. */
export function ListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card shadow-e1">
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="flex items-center gap-3 border-b border-border-soft px-4 py-3.5 last:border-b-0">
          <Skeleton className="size-2.5 rounded-full" />
          <Skeleton className="h-3.5 w-40 rounded-xs" />
          <Skeleton className="h-3 w-24 rounded-xs" />
          <Skeleton className="ml-auto h-5 w-16 rounded-full" />
        </div>
      ))}
    </div>
  )
}

/** Column shells with a few card-shaped blocks, so the board does not appear from nothing. */
export function BoardSkeleton({ columns = 5 }: { columns?: number }) {
  return (
    <div className="flex min-h-0 flex-1 gap-3 overflow-hidden pb-2">
      {Array.from({ length: columns }, (_, c) => (
        <div key={c} className="flex w-72 shrink-0 flex-col rounded-lg border border-border bg-surface-sunken">
          <div className="flex items-center gap-2 border-b border-border-soft px-3 py-3">
            <Skeleton className="size-2 rounded-full" />
            <Skeleton className="h-3 w-24 rounded-xs" />
            <Skeleton className="ml-auto h-4 w-6 rounded-full" />
          </div>
          <div className="flex flex-col gap-2 p-2">
            {Array.from({ length: 3 - (c % 2) }, (_, i) => (
              <div key={i} className="rounded-md border border-border bg-card p-3">
                <Skeleton className="h-3.5 w-full rounded-xs" />
                <Skeleton className="mt-2 h-3.5 w-2/3 rounded-xs" />
                <div className="mt-3 flex gap-1.5">
                  <Skeleton className="h-5 w-14 rounded-sm" />
                  <Skeleton className="h-5 w-20 rounded-sm" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Wraps a TanStack Query result so every screen handles loading, error and empty the same
 * way, instead of each one inventing its own.
 */
export function QueryBoundary<T>({
  query,
  children,
  empty,
  loadingLabel,
  skeleton,
}: {
  query: { data?: T; isPending: boolean; error: unknown; refetch: () => void }
  children: (data: T) => ReactNode
  empty?: ReactNode
  loadingLabel?: string
  /** Preferred over the spinner where the shape of the result is known up front. */
  skeleton?: ReactNode
}) {
  if (query.isPending) return <>{skeleton ?? <LoadingState label={loadingLabel} />}</>
  if (query.error) return <ErrorState error={query.error} onRetry={query.refetch} />
  if (query.data == null) return <>{empty ?? null}</>
  if (Array.isArray(query.data) && query.data.length === 0 && empty) return <>{empty}</>
  return <>{children(query.data)}</>
}
